import { useEffect, useState } from "react";
import { ArrowUpRight, X, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchHomepageContent } from "../lib/api";
import { track } from "../lib/analytics";

const STORAGE_KEY = "nexora_promo_dismissed";

function isWithinRange(start, end) {
  const now = new Date();
  if (start) {
    const s = new Date(start);
    if (!isNaN(s) && now < s) return false;
  }
  if (end) {
    const e = new Date(end);
    if (!isNaN(e) && now > e) return false;
  }
  return true;
}

/**
 * Site-wide promo alert bar driven by Homepage CMS fields.
 * Renders ONLY when promo_enabled=true AND today is within
 * promo_start_date..promo_end_date (both optional).
 */
export default function PromoAlertBar() {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchHomepageContent()
      .then((h) => setPromo(h))
      .catch((err) => {
        setPromo(null); // safe fallback: bar simply doesn't render
        if (process.env.NODE_ENV === "development") {
          console.warn("PromoAlertBar: homepage content unavailable", err?.message);
        }
      });
    try {
      setDismissed(sessionStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      // ignore
    }
  }, []);

  if (!promo || !promo.promo_enabled) return null;
  if (!isWithinRange(promo.promo_start_date, promo.promo_end_date)) return null;
  if (dismissed) return null;

  const bg = promo.promo_bg_color || "#FF8C42";
  const text = promo.promo_text_color || "#FFFFFF";
  const url = promo.promo_button_url || "/trial-class";
  const isExternal = url.startsWith("http://") || url.startsWith("https://");

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
  };

  return (
    <div
      data-testid="promo-alert-bar"
      className="w-full"
      style={{ backgroundColor: bg, color: text }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="flex items-center gap-2 font-semibold">
          <Tag className="h-4 w-4 shrink-0" />
          {promo.promo_title && (
            <span className="font-bold mr-1">{promo.promo_title}:</span>
          )}
          <span className="font-normal opacity-95">{promo.promo_message}</span>
        </span>
        <div className="ml-auto flex items-center gap-2">
          {promo.promo_button_text && (
            isExternal ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("promo_cta_clicked", { cta_target: url })}
                data-testid="promo-alert-cta"
                className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-bold bg-white"
                style={{ color: "#123B6D" }}
              >
                {promo.promo_button_text}
                <ArrowUpRight className="h-3 w-3" />
              </a>
            ) : (
              <Link
                to={url}
                onClick={() => track("promo_cta_clicked", { cta_target: url })}
                data-testid="promo-alert-cta"
                className="inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-xs font-bold bg-white"
                style={{ color: "#123B6D" }}
              >
                {promo.promo_button_text}
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            )
          )}
          <button
            onClick={dismiss}
            aria-label="Dismiss promo"
            data-testid="promo-alert-dismiss"
            className="opacity-80 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
